import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useProject } from '../context/ProjectContext';

/**
 * Users page component.
 *
 * Displays a list of users with filtering, pagination, and bulk operations.
 */
type SubscriptionStatus = 'trial' | 'active' | 'expired' | 'cancelled' | 'paused' | 'past_due';
type RegistrationPath = 'direct_message' | 'direct_whatsapp' | 'external_involve' | 'web_onboarding';

// Bulk action status type
type BulkActionStatus = 'idle' | 'updating' | 'success' | 'error';

// Risk level type
type RiskLevel = 'low' | 'medium' | 'high' | 'none';

// StatusBadge component with specific rgba colors from design
function StatusBadge({ status }: { status: string }) {
  const statusStyles: Record<string, { bg: string; text: string }> = {
    active: { bg: 'rgba(152, 184, 154, 0.15)', text: '#5A8A5E' },
    trial: { bg: 'rgba(184, 196, 232, 0.2)', text: '#5A6A8A' },
    pending_activation: { bg: 'rgba(232, 200, 140, 0.2)', text: '#9B7B3C' },
    expired: { bg: 'rgba(232, 200, 140, 0.2)', text: '#9B7B3C' },
    churned: { bg: 'rgba(201, 140, 130, 0.15)', text: '#8B5A50' },
    cancelled: { bg: 'rgba(201, 140, 130, 0.15)', text: '#8B5A50' },
    past_due: { bg: 'rgba(232, 180, 100, 0.2)', text: '#B87A30' },
    paused: { bg: 'rgba(93, 86, 78, 0.1)', text: '#6B645C' },
  };

  const s = statusStyles[status] || { bg: 'rgba(93, 86, 78, 0.08)', text: '#9B9285' };

  return (
    <span
      style={{
        padding: '4px 10px',
        borderRadius: '100px',
        background: s.bg,
        color: s.text,
        fontSize: '10px',
        fontWeight: 600,
        textTransform: 'uppercase',
        fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

// RiskBadge component with border styling from design
function RiskBadge({ risk }: { risk: RiskLevel }) {
  const riskColors: Record<RiskLevel, { bg: string; text: string; border: string }> = {
    low: { bg: 'rgba(152, 184, 154, 0.15)', text: '#5A8A5E', border: 'rgba(152, 184, 154, 0.3)' },
    medium: { bg: 'rgba(232, 200, 140, 0.15)', text: '#9B7B3C', border: 'rgba(232, 200, 140, 0.3)' },
    high: { bg: 'rgba(201, 140, 130, 0.15)', text: '#8B5A50', border: 'rgba(201, 140, 130, 0.3)' },
    none: { bg: 'rgba(93, 86, 78, 0.08)', text: '#9B9285', border: 'rgba(93, 86, 78, 0.15)' },
  };

  const c = riskColors[risk] || riskColors.none;

  return (
    <span
      style={{
        padding: '4px 10px',
        borderRadius: '100px',
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {risk}
    </span>
  );
}

// EngagementScore component with color coding
function EngagementScore({ score }: { score: number }) {
  const getScoreColor = (value: number): string => {
    if (value > 6) return '#5A8A5E';
    if (value > 4) return '#9B7B3C';
    return '#8B5A50';
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <span
        style={{
          fontSize: '14px',
          fontWeight: 600,
          color: getScoreColor(score),
        }}
      >
        {score}
      </span>
      <span style={{ fontSize: '11px', color: '#9B9285' }}>/ 10</span>
    </div>
  );
}

function formatRegistrationPath(value?: string | null): string {
  const labels: Record<string, string> = {
    direct_message: 'Direct',
    direct_whatsapp: 'WhatsApp',
    external_involve: 'Involve.me',
    web_onboarding: 'Web',
  };
  return value ? labels[value] || value : '-';
}

// Checkbox component for consistent styling
function Checkbox({
  checked,
  indeterminate,
  onChange,
  disabled,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) {
          onChange(!checked);
        }
      }}
      style={{
        width: '18px',
        height: '18px',
        borderRadius: '4px',
        border: `1.5px solid ${checked || indeterminate ? '#5A8A5E' : 'rgba(93, 86, 78, 0.25)'}`,
        background: checked || indeterminate ? '#5A8A5E' : 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s ease',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {checked && (
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {indeterminate && !checked && (
        <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
          <path d="M1 1H9" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>
      )}
    </div>
  );
}

// Plan type
type Plan = {
  id: string;
  name: string;
  planKey: string;
};

// Bulk Action Bar component
function BulkActionBar({
  selectedCount,
  onUpdateStatus,
  onUpdatePlan,
  onClearSelection,
  status,
  result,
  plans,
}: {
  selectedCount: number;
  onUpdateStatus: (status: SubscriptionStatus) => void;
  onUpdatePlan: (planId: string | null) => void;
  onClearSelection: () => void;
  status: BulkActionStatus;
  result: { successCount: number; failureCount: number } | null;
  plans: Plan[];
}) {
  const [selectedStatus, setSelectedStatus] = useState<SubscriptionStatus | ''>('');
  const [selectedPlan, setSelectedPlan] = useState<string>('');

  const handleApplyStatus = () => {
    if (selectedStatus) {
      onUpdateStatus(selectedStatus);
      setSelectedStatus('');
    }
  };

  const handleApplyPlan = () => {
    if (selectedPlan) {
      onUpdatePlan(selectedPlan === '__none__' ? null : selectedPlan);
      setSelectedPlan('');
    }
  };

  return (
    <div style={bulkActionStyles.container}>
      <div style={bulkActionStyles.leftSection}>
        <span style={bulkActionStyles.selectedText}>
          {selectedCount} user{selectedCount !== 1 ? 's' : ''} selected
        </span>
        <button onClick={onClearSelection} style={bulkActionStyles.clearButton}>
          Clear selection
        </button>
      </div>
      <div style={bulkActionStyles.rightSection}>
        {status === 'success' && result && (
          <span style={bulkActionStyles.successText}>
            Updated! {result.successCount} success{result.failureCount > 0 ? `, ${result.failureCount} failed` : ''}
          </span>
        )}
        {status === 'error' && (
          <span style={bulkActionStyles.errorText}>Update failed. Please try again.</span>
        )}
        {status !== 'success' && (
          <>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as SubscriptionStatus | '')}
              style={bulkActionStyles.select}
              disabled={status === 'updating'}
            >
              <option value="">Change status to...</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
              <option value="paused">Paused</option>
              <option value="past_due">Past Due</option>
            </select>
            <button
              onClick={handleApplyStatus}
              disabled={!selectedStatus || status === 'updating'}
              style={{
                ...bulkActionStyles.applyButton,
                opacity: !selectedStatus || status === 'updating' ? 0.5 : 1,
                cursor: !selectedStatus || status === 'updating' ? 'not-allowed' : 'pointer',
              }}
            >
              {status === 'updating' ? 'Updating...' : 'Apply'}
            </button>
            <div style={{ width: '1px', height: '24px', backgroundColor: 'rgba(93, 86, 78, 0.15)', margin: '0 4px' }} />
            <select
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              style={bulkActionStyles.select}
              disabled={status === 'updating'}
            >
              <option value="">Change plan to...</option>
              <option value="__none__">No Plan</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </select>
            <button
              onClick={handleApplyPlan}
              disabled={!selectedPlan || status === 'updating'}
              style={{
                ...bulkActionStyles.applyButton,
                opacity: !selectedPlan || status === 'updating' ? 0.5 : 1,
                cursor: !selectedPlan || status === 'updating' ? 'not-allowed' : 'pointer',
              }}
            >
              {status === 'updating' ? 'Updating...' : 'Apply'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const bulkActionStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    backgroundColor: 'rgba(90, 138, 94, 0.08)',
    borderRadius: '10px',
    marginBottom: '16px',
    border: '1px solid rgba(90, 138, 94, 0.2)',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  selectedText: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  clearButton: {
    padding: '6px 12px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    color: '#5A8A5E',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  select: {
    padding: '8px 12px',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    borderRadius: '8px',
    fontSize: '13px',
    fontFamily: 'inherit',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none',
    minWidth: '160px',
  },
  applyButton: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#5A8A5E',
    color: 'white',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: 'inherit',
    transition: 'background 0.15s ease',
  },
  successText: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#5A8A5E',
  },
  errorText: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#8B5A50',
  },
};

export function UsersPage() {
  const navigate = useNavigate();
  const { currentBotId } = useProject();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | ''>('');
  const [registrationPathFilter, setRegistrationPathFilter] = useState<RegistrationPath | ''>('');
  const [utmSourceFilter, setUtmSourceFilter] = useState('');
  const [utmCampaignFilter, setUtmCampaignFilter] = useState('');
  const [quizOutcomeFilter, setQuizOutcomeFilter] = useState('');
  const [onlyActiveFilter, setOnlyActiveFilter] = useState(true);
  const [offset, setOffset] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [bulkActionStatus, setBulkActionStatus] = useState<BulkActionStatus>('idle');
  const [bulkActionResult, setBulkActionResult] = useState<{ successCount: number; failureCount: number } | null>(null);
  const [resetHistoryUserId, setResetHistoryUserId] = useState<string | null>(null);
  const limit = 20;

  // Export users CSV mutation
  const exportQuery = trpc.export.generateUsersCsv.useQuery(
    { botId: currentBotId ?? undefined },
    { enabled: false }
  );

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const result = await exportQuery.refetch();
      if (result.data) {
        // Create blob and trigger download
        const blob = new Blob([result.data.csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const usersQuery = trpc.users.list.useQuery(
    {
      search: search || undefined,
      subscriptionStatus: statusFilter || undefined,
      registrationPath: registrationPathFilter || undefined,
      utmSource: utmSourceFilter.trim() || undefined,
      utmCampaign: utmCampaignFilter.trim() || undefined,
      quizOutcome: quizOutcomeFilter.trim() || undefined,
      onlyActive: onlyActiveFilter,
      offset,
      limit,
      botId: currentBotId ?? undefined,
    },
    { enabled: !!currentBotId }
  );

  // Fetch plans for the current bot
  const plansQuery = trpc.plans.list.useQuery(
    { botId: currentBotId ?? undefined, status: 'active' },
    { enabled: !!currentBotId }
  );

  // Bulk update mutation
  const bulkUpdateMutation = trpc.users.bulkUpdate.useMutation({
    onSuccess: (data) => {
      setBulkActionStatus('success');
      setBulkActionResult({ successCount: data.successCount, failureCount: data.failureCount });
      // Clear selection and refetch after success
      setTimeout(() => {
        setSelectedUserIds(new Set());
        setBulkActionStatus('idle');
        setBulkActionResult(null);
        usersQuery.refetch();
      }, 2000);
    },
    onError: () => {
      setBulkActionStatus('error');
      setTimeout(() => {
        setBulkActionStatus('idle');
      }, 3000);
    },
  });

  // Selection helpers
  const currentPageUserIds = useMemo(
    () => usersQuery.data?.users.map((u) => u.id) ?? [],
    [usersQuery.data?.users]
  );

  const selectedOnCurrentPage = useMemo(
    () => currentPageUserIds.filter((id) => selectedUserIds.has(id)),
    [currentPageUserIds, selectedUserIds]
  );

  const isAllSelected = currentPageUserIds.length > 0 && selectedOnCurrentPage.length === currentPageUserIds.length;
  const isSomeSelected = selectedOnCurrentPage.length > 0 && selectedOnCurrentPage.length < currentPageUserIds.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUserIds((prev) => {
        const newSet = new Set(prev);
        currentPageUserIds.forEach((id) => newSet.add(id));
        return newSet;
      });
    } else {
      setSelectedUserIds((prev) => {
        const newSet = new Set(prev);
        currentPageUserIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    setSelectedUserIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(userId);
      } else {
        newSet.delete(userId);
      }
      return newSet;
    });
  };

  const handleClearSelection = () => {
    setSelectedUserIds(new Set());
    setBulkActionStatus('idle');
    setBulkActionResult(null);
  };

  const handleBulkUpdateStatus = (status: SubscriptionStatus) => {
    if (selectedUserIds.size === 0) return;
    setBulkActionStatus('updating');
    bulkUpdateMutation.mutate({
      userIds: Array.from(selectedUserIds),
      subscriptionStatus: status,
    });
  };

  const handleBulkUpdatePlan = (planId: string | null) => {
    if (selectedUserIds.size === 0) return;
    setBulkActionStatus('updating');
    bulkUpdateMutation.mutate({
      userIds: Array.from(selectedUserIds),
      subscriptionPlanId: planId,
    });
  };

  // Reset history mutation
  const resetHistoryMutation = trpc.users.resetHistory.useMutation({
    onSuccess: () => {
      setResetHistoryUserId(null);
      usersQuery.refetch();
      window.alert('User history has been reset successfully. All messages, profile data, goals, and engagement metrics have been wiped. The user is now in a completely NEW state.');
    },
    onError: () => {
      setResetHistoryUserId(null);
    },
  });

  const handleViewUser = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  const handleResetHistory = (userId: string, userName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      `Are you sure you want to reset the history for ${userName || 'this user'}? This will delete ALL messages, user profile, goals, and engagement metrics, resetting the user to a completely NEW state. This action is IRREVERSIBLE.`
    );
    if (confirmed) {
      setResetHistoryUserId(userId);
      resetHistoryMutation.mutate({ userId });
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Users</h1>
          <div style={styles.stats}>
            {usersQuery.data && (
              <span style={styles.count}>
                {usersQuery.data.pagination.total.toLocaleString()} total users
              </span>
            )}
          </div>
        </div>
        <button
          onClick={handleExport}
          disabled={isExporting || !currentBotId}
          style={styles.exportButton}
        >
          {isExporting ? 'Exporting...' : 'Export Data'}
        </button>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOffset(0);
          }}
          style={styles.searchInput}
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as SubscriptionStatus | '');
            setOffset(0);
          }}
          style={styles.select}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
          <option value="paused">Paused</option>
          <option value="past_due">Past Due</option>
        </select>
        <select
          value={registrationPathFilter}
          onChange={(e) => {
            setRegistrationPathFilter(e.target.value as RegistrationPath | '');
            setOffset(0);
          }}
          style={styles.select}
        >
          <option value="">All Sources</option>
          <option value="external_involve">Involve.me</option>
          <option value="direct_whatsapp">Direct WhatsApp</option>
          <option value="web_onboarding">Web Onboarding</option>
          <option value="direct_message">Direct Message</option>
        </select>
        <input
          type="text"
          placeholder="UTM source"
          value={utmSourceFilter}
          onChange={(e) => {
            setUtmSourceFilter(e.target.value);
            setOffset(0);
          }}
          style={styles.compactFilterInput}
        />
        <input
          type="text"
          placeholder="Campaign"
          value={utmCampaignFilter}
          onChange={(e) => {
            setUtmCampaignFilter(e.target.value);
            setOffset(0);
          }}
          style={styles.compactFilterInput}
        />
        <input
          type="text"
          placeholder="Outcome"
          value={quizOutcomeFilter}
          onChange={(e) => {
            setQuizOutcomeFilter(e.target.value);
            setOffset(0);
          }}
          style={styles.compactFilterInput}
        />
        <label
          style={styles.checkboxFilter}
          onClick={() => {
            setOnlyActiveFilter((checked) => !checked);
            setOffset(0);
          }}
        >
          <Checkbox
            checked={onlyActiveFilter}
            onChange={(checked) => {
              setOnlyActiveFilter(checked);
              setOffset(0);
            }}
          />
          <span>Only active</span>
        </label>
      </div>

      {/* Bulk Action Bar */}
      {selectedUserIds.size > 0 && (
        <BulkActionBar
          selectedCount={selectedUserIds.size}
          onUpdateStatus={handleBulkUpdateStatus}
          onUpdatePlan={handleBulkUpdatePlan}
          onClearSelection={handleClearSelection}
          status={bulkActionStatus}
          result={bulkActionResult}
          plans={plansQuery.data?.plans ?? []}
        />
      )}

      {/* Users Table */}
      {usersQuery.isLoading ? (
        <div style={styles.loading}>Loading users...</div>
      ) : usersQuery.error ? (
        <div style={styles.error}>
          Error loading users: {usersQuery.error.message}
        </div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            {/* Table Header */}
            <div style={styles.tableHeader}>
              <div style={styles.checkboxCell}>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isSomeSelected}
                  onChange={handleSelectAll}
                  disabled={currentPageUserIds.length === 0}
                />
              </div>
              {['User', 'Acquisition', 'Status', 'Plan', 'Engagement', 'Risk', 'Revenue', 'Cost', 'Last Active', 'Actions'].map((col) => (
                <span key={col} style={styles.th}>
                  {col}
                </span>
              ))}
            </div>

            {/* Table Body */}
            {usersQuery.data?.users.length === 0 ? (
              <div style={styles.empty}>No users found</div>
            ) : (
              usersQuery.data?.users.map((user, index) => (
                <div
                  key={user.id}
                  onClick={() => handleViewUser(user.id)}
                  style={{
                    ...styles.tableRow,
                    borderBottom:
                      index < (usersQuery.data?.users.length ?? 0) - 1
                        ? '1px solid rgba(93, 86, 78, 0.04)'
                        : 'none',
                    backgroundColor: selectedUserIds.has(user.id)
                      ? 'rgba(90, 138, 94, 0.04)'
                      : 'transparent',
                  }}
                >
                  {/* Checkbox Cell */}
                  <div style={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedUserIds.has(user.id)}
                      onChange={(checked) => handleSelectUser(user.id, checked)}
                    />
                  </div>

                  {/* User Cell */}
                  <div style={styles.userCell}>
                    <div style={styles.avatar}>
                      {user.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p style={styles.userName}>{user.name || 'Unknown'}</p>
                      <p style={styles.userEmail}>
                        {user.phoneNumber || `ID: ${user.id}`}
                      </p>
                      {(user.stripeCheckoutAccessCode || user.stripeCustomerEmail) && (
                        <p style={styles.userStripeInfo}>
                          {[
                            user.stripeCheckoutAccessCode ? `Code: ${user.stripeCheckoutAccessCode}` : null,
                            user.stripeCustomerEmail || null,
                          ].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Acquisition Cell */}
                  <div style={styles.acquisitionCell}>
                    <span style={styles.acquisitionPath}>
                      {formatRegistrationPath(user.registrationPath)}
                    </span>
                    <span style={styles.acquisitionMeta}>
                      {[
                        user.utmSource || null,
                        user.utmCampaign || null,
                        user.quizOutcome || null,
                      ].filter(Boolean).join(' · ') || '-'}
                    </span>
                  </div>

                  {/* Status Cell */}
                  <div style={styles.cellCenter}>
                    <StatusBadge status={user.subscriptionStatus ?? 'unknown'} />
                  </div>

                  {/* Plan Cell */}
                  <div style={styles.cellCenter}>
                    <span style={styles.planDisplay}>
                      {plansQuery.data?.plans.find((plan) => plan.id === user.subscriptionPlanId)?.name || 'No Plan'}
                    </span>
                  </div>

                  {/* Engagement Cell */}
                  <div style={styles.cellCenter}>
                    <EngagementScore score={user.engagementScore ?? 0} />
                  </div>

                  {/* Risk Cell */}
                  <div style={styles.cellCenter}>
                    <RiskBadge risk={(user.churnRisk as RiskLevel) ?? 'none'} />
                  </div>

                  {/* Revenue Cell */}
                  <div style={styles.cellCenter}>
                    <span style={styles.monetaryValue}>
                      ${((user.lifetimeRevenue ?? 0) / 100).toFixed(2)}
                    </span>
                  </div>

                  {/* Cost Cell */}
                  <div style={styles.cellCenter}>
                    <span style={styles.monetaryValueCost}>
                      ${((user.lifetimeCost ?? 0) / 100).toFixed(2)}
                    </span>
                  </div>

                  {/* Last Active Cell */}
                  <div style={styles.cellCenter}>
                    <span style={styles.lastActive}>
                      {user.lastMessageAt
                        ? new Date(user.lastMessageAt).toLocaleDateString()
                        : 'Never'}
                    </span>
                  </div>

                  {/* Actions Cell */}
                  <div style={styles.actionsCell}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewUser(user.id);
                      }}
                      style={styles.actionButton}
                      title="View Details"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => handleResetHistory(user.id, user.name || 'Unknown', e)}
                      style={{
                        ...styles.actionButton,
                        ...styles.actionButtonDanger,
                        ...(resetHistoryUserId === user.id ? styles.actionButtonDisabled : {}),
                      }}
                      disabled={resetHistoryUserId === user.id}
                      title="Reset History"
                    >
                      {resetHistoryUserId === user.id ? (
                        <span style={styles.loadingSpinner}>...</span>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18" />
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                          <line x1="10" y1="11" x2="10" y2="17" />
                          <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {usersQuery.data && usersQuery.data.pagination.hasMore && (
            <div style={styles.pagination}>
              <button
                onClick={() => setOffset((o) => Math.max(0, o - limit))}
                disabled={offset === 0}
                style={styles.pageButton}
              >
                Previous
              </button>
              <span style={styles.pageInfo}>
                Showing {offset + 1} - {Math.min(offset + limit, usersQuery.data.pagination.total)} of {usersQuery.data.pagination.total}
              </span>
              <button
                onClick={() => setOffset((o) => o + limit)}
                disabled={!usersQuery.data.pagination.hasMore}
                style={styles.pageButton}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}


const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '1200px',
    fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  exportButton: {
    padding: '10px 20px',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    borderRadius: '10px',
    backgroundColor: '#5A8A5E',
    color: 'white',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    transition: 'background 0.15s ease, opacity 0.15s ease',
  },
  title: {
    fontFamily: '"Sentient", Georgia, serif',
    fontSize: '24px',
    fontWeight: 400,
    color: '#3D3A36',
    marginBottom: '4px',
  },
  stats: {
    fontSize: '13px',
    color: '#9B9285',
  },
  count: {
    fontWeight: 500,
  },
  filters: {
    display: 'flex',
    gap: '10px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1,
    maxWidth: '180px',
    padding: '10px 14px',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    borderRadius: '10px',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    background: 'white',
  },
  compactFilterInput: {
    width: '140px',
    padding: '10px 14px',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    borderRadius: '10px',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    background: 'white',
  },
  select: {
    padding: '10px 14px',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    borderRadius: '10px',
    fontSize: '13px',
    fontFamily: 'inherit',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none',
  },
  checkboxFilter: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    borderRadius: '10px',
    backgroundColor: 'white',
    color: '#5C564E',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    userSelect: 'none',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#9B9285',
  },
  error: {
    padding: '16px',
    backgroundColor: 'rgba(201, 140, 130, 0.15)',
    color: '#8B5A50',
    borderRadius: '10px',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '16px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '40px 1.6fr 1.2fr 0.8fr 1.1fr 0.7fr 0.7fr 0.7fr 0.7fr 0.9fr 100px',
    padding: '14px 20px',
    background: 'rgba(93, 86, 78, 0.02)',
    borderBottom: '1px solid rgba(93, 86, 78, 0.06)',
    alignItems: 'center',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '40px 1.6fr 1.2fr 0.8fr 1.1fr 0.7fr 0.7fr 0.7fr 0.7fr 0.9fr 100px',
    padding: '14px 20px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  },
  checkboxCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  th: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#9B9285',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  cellCenter: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #E8D5C5 0%, rgba(232, 213, 197, 0.8) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 600,
    color: '#5C564E',
  },
  userName: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#3D3A36',
    margin: 0,
  },
  userEmail: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  userStripeInfo: {
    fontSize: '11px',
    color: '#5A8A5E',
    margin: '2px 0 0',
    wordBreak: 'break-word',
  },
  acquisitionCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
  },
  acquisitionPath: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  acquisitionMeta: {
    marginTop: '3px',
    fontSize: '11px',
    color: '#9B9285',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  lastActive: {
    fontSize: '13px',
    color: '#5C564E',
  },
  monetaryValue: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#5A8A5E',
  },
  monetaryValueCost: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#8B5A50',
  },
  planDisplay: {
    fontSize: '12px',
    color: '#5C564E',
    fontWeight: 500,
  },
  empty: {
    padding: '40px',
    textAlign: 'center',
    color: '#9B9285',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '24px',
  },
  pageButton: {
    padding: '10px 16px',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    borderRadius: '10px',
    backgroundColor: 'white',
    fontSize: '13px',
    fontFamily: 'inherit',
    cursor: 'pointer',
    color: '#5C564E',
    transition: 'background 0.15s ease',
  },
  pageInfo: {
    fontSize: '13px',
    color: '#9B9285',
  },
  actionsCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    borderRadius: '6px',
    backgroundColor: 'white',
    color: '#5C564E',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  actionButtonDanger: {
    color: '#8B5A50',
    borderColor: 'rgba(139, 90, 80, 0.3)',
  },
  actionButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  loadingSpinner: {
    fontSize: '10px',
    fontWeight: 'bold',
  },
};

export default UsersPage;
